import { defineStore } from 'pinia'

import { getPublicSettings } from '@/api/settings'
import { setGlobalCurrency } from '@/utils/formatters'

const toBool = (v) => v === true || v === 1 || v === '1' || v === 'true'

export const useSettingsStore = defineStore('settings', {
  state: () => ({
    currency: 'NGN',
    hotelName: '',
    phone: '',
    email: '',
    address: '',
    vatEnabled: false,
    vatPercentage: 0,
    horcEnabled: false,
    horcPercentage: 0,
    paystackEnabled: true,
    bankTransferEnabled: false,
    bankName: '',
    bankAccountName: '',
    bankAccountNumber: '',
    bankInstructions: '',
    social: {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: '',
      youtube: '',
      tiktok: '',
      whatsapp: ''
    },
    loaded: false
  }),

  getters: {
    vatRate: (s) => (s.vatEnabled ? Number(s.vatPercentage) || 0 : 0),
    horcRate: (s) => (s.horcEnabled ? Number(s.horcPercentage) || 0 : 0),
    // Builds the tax breakdown lines for an invoice/receipt from the already
    // separated VAT (accommodation) and HORC (consumption) amounts. VAT applies
    // only to reservation charges; HORC only to orders, laundry and folio
    // charges, so they are never apportioned from a single figure. Returns a
    // line per non-zero tax: { key, label, rate, amount }.
    taxBreakdown() {
      const vatRate = this.vatRate
      const horcRate = this.horcRate
      return (vatAmount = 0, horcAmount = 0) => {
        const lines = []
        const vat = Math.round((Number(vatAmount) || 0) * 100) / 100
        const horc = Math.round((Number(horcAmount) || 0) * 100) / 100
        if (vat > 0) lines.push({ key: 'vat', label: 'VAT', rate: vatRate, amount: vat })
        if (horc > 0) lines.push({ key: 'horc', label: 'HORC', rate: horcRate, amount: horc })
        return lines
      }
    }
  },

  actions: {
    apply(data) {
      if (data.currency) this.currency = data.currency
      this.hotelName = data.hotel_name || this.hotelName
      this.phone = data.phone || this.phone
      this.email = data.email || this.email
      this.address = data.address || this.address
      if ('vat_enabled' in data) this.vatEnabled = toBool(data.vat_enabled)
      if ('vat_percentage' in data) this.vatPercentage = Number(data.vat_percentage) || 0
      if ('horc_enabled' in data) this.horcEnabled = toBool(data.horc_enabled)
      if ('horc_percentage' in data) this.horcPercentage = Number(data.horc_percentage) || 0
      if ('paystack_enabled' in data) this.paystackEnabled = toBool(data.paystack_enabled)
      if ('bank_transfer_enabled' in data)
        this.bankTransferEnabled = toBool(data.bank_transfer_enabled)
      this.bankName = data.bank_name ?? this.bankName
      this.bankAccountName = data.bank_account_name ?? this.bankAccountName
      this.bankAccountNumber = data.bank_account_number ?? this.bankAccountNumber
      this.bankInstructions = data.bank_instructions ?? this.bankInstructions
      this.social = {
        facebook: data.facebook_url || '',
        instagram: data.instagram_url || '',
        twitter: data.twitter_url || '',
        linkedin: data.linkedin_url || '',
        youtube: data.youtube_url || '',
        tiktok: data.tiktok_url || '',
        whatsapp: data.whatsapp_number || ''
      }
      setGlobalCurrency(this.currency)
    },

    async load() {
      try {
        const res = await getPublicSettings()
        this.apply(res.data.data || res.data || {})
      } catch (e) {
        // Keep defaults if settings can't be reached.
      } finally {
        this.loaded = true
      }
    }
  }
})
